/**
 * Module: `ui-helper/dates.ts` — client library utilities.
 * @remarks See exports; keep imports from concrete files when possible.
 */

import { isValid, parse } from "date-fns";

/** Sidoni meta dates: `dd/MM/yyyy` (legacy) or `yyyy-MM-dd` (ISO). */
export const SIDONI_META_DATE_MASK =
  /^(?:\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2})$/;

export function isSidoniMetaDateString(
  value: string | null | undefined
): value is string {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!SIDONI_META_DATE_MASK.test(trimmed)) return false;
  return parseSidoniMetaDate(trimmed) !== undefined;
}

function parseSidoniMetaDate(date: string): Date | undefined {
  for (const fmt of ["dd/MM/yyyy", "yyyy-MM-dd"] as const) {
    const parsed = parse(date, fmt, new Date());
    if (isValid(parsed)) return parsed;
  }
  return undefined;
}

/** First valid publication/update date from Sidoni document metadata. */
export function pickSidoniPublicationDate(
  datePublication?: string | null,
  dateMiseAJour?: string | null
): string | undefined {
  for (const raw of [datePublication, dateMiseAJour]) {
    if (isSidoniMetaDateString(raw)) return raw.trim();
  }
  return undefined;
}

/**
 * Parse a date string from the API as UTC when it has no timezone.
 * PostgreSQL "timestamp" returns "YYYY-MM-DD HH:mm:ss" (no Z); "timestamptz" returns ISO with Z.
 * This ensures we always display the correct instant in the user's locale (e.g. Europe/Paris).
 *
 * Returns `new Date(0)` for null/undefined/empty/unparseable input so callers
 * never receive an Invalid Date.
 */
export function parseUtcDate(s: string | null | undefined): Date {
  if (!s) return new Date(0);
  const trimmed = s.trim();
  if (!trimmed) return new Date(0);

  // ISO with trailing Z (incl. fractional seconds) or explicit offset (+00:00, +0200, …).
  const hasExplicitTimezone = /(?:Z|[+-]\d{2}(?::\d{2})?)$/i.test(trimmed);
  const parsed = hasExplicitTimezone
    ? new Date(trimmed)
    : new Date(trimmed.replace(" ", "T") + "Z");

  return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
}

export const formatDate = (date: Date) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return `Aujourd'hui ${date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } else if (date.toDateString() === yesterday.toDateString()) {
    return `Hier ${date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } else {
    return `Le ${date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })}`;
  }
};

const FR_TIME_HM: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
};

/**
 * Display instant for an assistant message: when generation finished.
 * Prefers Dust `completedTs`, else `created + completionDurationMs`, else `created`.
 */
export function resolveAgentMessageDisplayTimestamp(input: {
  created: Date;
  completedTs?: number | null;
  completionDurationMs?: number | null;
}): Date {
  const { created, completedTs, completionDurationMs } = input;
  if (
    typeof completedTs === "number" &&
    Number.isFinite(completedTs) &&
    completedTs > 0
  ) {
    return new Date(completedTs);
  }
  if (
    typeof completionDurationMs === "number" &&
    Number.isFinite(completionDurationMs) &&
    completionDurationMs >= 0
  ) {
    return new Date(created.getTime() + completionDurationMs);
  }
  return created;
}

function formatCalendarDayMonthYear(date: Date): string {
  const day = date.getDate();
  const dayPart = day === 1 ? "1er" : String(day);
  const monthYear = date.toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
  return `${dayPart} ${monthYear}`;
}

/**
 * Shared date+time fragment for chat meta and conversation cards.
 * - today / yesterday: `aujourd'hui à HH:mm` / `hier à HH:mm`
 * - else: `le D mois AAAA à HH:mm` (`1er` for the 1st)
 *
 * Cards compose as `Créé ${fragment}` / `Dernière activité ${fragment}`
 * (fragment already carries `le` on calendar dates).
 */
export function formatConversationMetaDate(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const time = date.toLocaleTimeString("fr-FR", FR_TIME_HM);

  if (date.toDateString() === today.toDateString()) {
    return `aujourd'hui à ${time}`;
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return `hier à ${time}`;
  }
  return `le ${formatCalendarDayMonthYear(date)} à ${time}`;
}

/**
 * Show « Dernière activité » only when its visible label differs from « Créé ».
 * Compares formatted fragments (day + minute), not raw milliseconds.
 */
export function shouldShowConversationLastActivityLabel(
  createdLabel: string | null,
  activityLabel: string | null
): boolean {
  return (
    Boolean(activityLabel) && (!createdLabel || activityLabel !== createdLabel)
  );
}

export type ChatMessageMetaLabelInput = {
  role: "user" | "assistant";
  timestamp: Date;
  status?: "created" | "succeeded" | "failed" | "cancelled";
  completionDurationMs?: number;
  assistantOrigin?: "gatekeeper";
};

/**
 * Full chat meta line: role/status prefix + shared date fragment + optional duration.
 */
export function formatChatMessageMetaLabel(
  input: ChatMessageMetaLabelInput
): string {
  const datePart = formatConversationMetaDate(input.timestamp);
  const durationMs = input.completionDurationMs;
  const hasDuration =
    typeof durationMs === "number" &&
    Number.isFinite(durationMs) &&
    durationMs > 0;
  const durationLabel = hasDuration
    ? formatGenerationDurationMs(durationMs)
    : null;

  if (input.role === "user") {
    return `Question créée ${datePart}`;
  }

  const subject =
    input.assistantOrigin === "gatekeeper" ? "Clarification" : "Réponse";

  if (input.status === "created") {
    return `${subject} en cours ${datePart}`;
  }
  if (input.status === "failed") {
    return durationLabel
      ? `${subject} échouée après ${durationLabel} · ${datePart}`
      : `${subject} échouée · ${datePart}`;
  }
  if (input.status === "cancelled") {
    return durationLabel
      ? `${subject} interrompue après ${durationLabel} · ${datePart}`
      : `${subject} interrompue · ${datePart}`;
  }

  return durationLabel
    ? `${subject} terminée ${datePart} · en ${durationLabel}`
    : `${subject} terminée ${datePart}`;
}

/**
 * Format a duration in milliseconds as minutes and seconds (e.g. "11 min 24 s", "45 s").
 */
export function formatGenerationDurationMs(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) {
    return `${seconds} s`;
  }
  if (seconds === 0) {
    return `${minutes} minutes`;
  }
  return `${minutes} minutes et ${seconds} secondes`;
}
